import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/LanguageToggle";
import { HowItWorks } from "@/components/HowItWorks";
import { useI18n } from "@/i18n";
import { localizedBranding } from "@/hooks/useBranding";
import {
  GraduationCap,
  Sparkles,
  Target,
  BarChart3,
  Clock,
  ArrowRight,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";

// Higher-resolution crop so the hero stays sharp on retina screens.
const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1551836022-b06985bceb24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxleGVjdXRpdmUlMjBjb2FjaGluZyUyMGNvcnBvcmF0ZSUyMGJ1c2luZXNzJTIwbGVhZGVyfGVufDB8fHx8MTc5MDMzNDMxNnww&ixlib=rb-4.1.0&q=92&w=1800&h=1200&fit=crop&dpr=2";

const VALUE_PROPS = [
  { icon: Target, k: "vp1" },
  { icon: Sparkles, k: "vp2" },
  { icon: Clock, k: "vp3" },
  { icon: BarChart3, k: "vp4" },
];

export function Landing({ onStart, branding = {}, logoUrl = "" }) {
  const { t, lang } = useI18n();
  const [hiwOpen, setHiwOpen] = useState(false);
  const brandName = branding.brand_name || "CoachScore";
  const headline = localizedBranding(branding, "headline", lang) || t("landing.headline");
  const subheadline = localizedBranding(branding, "subheadline", lang) || t("landing.sub");
  const brandColor = branding.brand_color || "#6366F1";

  const cta = (testid, extra = "") => (
    <Button
      onClick={onStart}
      style={{ backgroundColor: brandColor }}
      className={`text-white hover:opacity-90 transition-opacity duration-200 shadow-lg shadow-slate-400/30 ${extra}`}
      data-testid={testid}
    >
      {t("landing.cta")}
      <ArrowRight size={18} className="ml-2" />
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#DCE4EF] text-slate-900" data-testid="landing-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <span className="inline-flex items-center rounded-2xl bg-white px-5 py-3.5 shadow-md shadow-slate-400/30">
              <img
                src={logoUrl}
                alt={brandName}
                className="h-16 sm:h-20 max-w-[280px] object-contain"
                data-testid="brand-logo"
              />
            </span>
          ) : (
            <>
              <span
                className="flex h-12 w-12 items-center justify-center rounded-xl shadow-md"
                style={{ background: brandColor }}
              >
                <GraduationCap size={24} className="text-white" />
              </span>
              <div>
                <h1 className="font-heading text-xl font-bold leading-none tracking-tight text-slate-900">
                  {brandName}
                </h1>
                <p className="text-[11px] text-slate-500 leading-none mt-1">{t("brand.tagline")}</p>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle light />
          {cta("nav-start-btn", "h-9 px-4 hidden sm:inline-flex")}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-16 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-6 bg-white"
              style={{ color: brandColor, border: `1px solid ${brandColor}59` }}
            >
              <Sparkles size={13} /> {t("landing.badge")}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
              {headline}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mt-5 max-w-xl">{subheadline}</p>

            <ul className="mt-6 space-y-2.5">
              {["landing.check1", "landing.check2", "landing.check3"].map((k) => (
                <li key={k} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />
                  {t(k)}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {cta("hero-start-btn", "h-12 px-6 text-base")}
              <Button
                variant="outline"
                onClick={() => setHiwOpen(true)}
                className="h-12 px-5 text-base bg-white border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 transition-colors duration-200"
                data-testid="how-it-works-btn"
              >
                <PlayCircle size={18} className="mr-2" style={{ color: brandColor }} />
                {t("hiw.cta")}
              </Button>
              <span className="text-xs text-slate-500 w-full sm:w-auto">{t("landing.ctaNote")}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative"
          >
            <div
              className="absolute -inset-4 blur-2xl rounded-3xl"
              style={{ background: `linear-gradient(to top right, ${brandColor}26, #f59e0b1f)` }}
            />
            <img
              src={DEFAULT_HERO}
              alt={t("landing.heroAlt")}
              loading="eager"
              decoding="sync"
              className="relative rounded-2xl border border-white shadow-xl shadow-slate-400/40 w-full h-[340px] lg:h-[440px] object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 backdrop-blur-md bg-white/90 border border-slate-200 rounded-xl p-4 shadow-lg shadow-slate-900/10">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                    {t("landing.statReadiness")}
                  </p>
                  <p className="font-heading text-2xl font-extrabold text-slate-900">48%</p>
                </div>
                <div className="h-10 w-px bg-slate-300" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                    {t("landing.statTopGap")}
                  </p>
                  <p className="font-heading text-base font-bold text-rose-600">{t("landing.statGapValue")}</p>
                </div>
                <div className="h-10 w-px bg-slate-300" />
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                    {t("landing.statPrograms")}
                  </p>
                  <p className="font-heading text-2xl font-extrabold text-emerald-600">4</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUE_PROPS.map((v, i) => (
            <motion.div
              key={v.k}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm shadow-slate-400/20 transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <span
                className="flex h-11 w-11 items-center justify-center rounded-xl mb-4"
                style={{ backgroundColor: `${brandColor}1f` }}
              >
                <v.icon size={20} style={{ color: brandColor }} />
              </span>
              <h3 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-2">
                {t(`landing.${v.k}.title`)}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{t(`landing.${v.k}.desc`)}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 text-center">{cta("footer-start-btn", "h-12 px-8 text-base")}</div>
      </section>

      <HowItWorks
        open={hiwOpen}
        onOpenChange={setHiwOpen}
        onStart={onStart}
        brandColor={brandColor}
        videoUrl={branding.intro_video_url}
      />
    </div>
  );
}
